/* eslint-disable react/require-default-props */
import { Bill, BillSponsor, Hearing } from '@enview/interface/types/bills/Bill';
import BillSearchView from '@enview/interface/types/bills/BillSearchView';
import { BillSummary } from '@enview/interface/types/bills/BillSummary';
import {
  AI_TAGS_ATTRIBUTION_ID,
  parseAiTagsSourceName,
} from '@enview/interface/types/triage/AutoTriage';
import { getMostRecentAction, formatDate } from '../../../helpers/BillHelper';
import {
  MouseEventHandler,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Spinner } from 'react-bootstrap';
import TopBarProgress from 'react-topbar-progress-indicator';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  getOrganizationUser,
  getOrgForTeamMode,
  getTeamMode,
  updateOrganizationUser,
} from '../../../dux';
import { convertIdToAbbreviatedTitle, getBillAlias } from '../../../helpers/BillHelper';
import { SortByParam, SortOrder } from '../../../models/SortOrder';
import featureFlags, { FrontendFeature } from '../../../featureFlags';
import BillStatusBadge from '../../LegislativeTracking/BillStatusBadge';
import BillTagButton from '../BillTagging/BillTagButton';
import TrackColumnField from '../BillTracking/TrackColumnField';
import BillTrackingButton from '../BillTracking/BillTrackingButton';
import WorkspacesColumnField from './WorkspacesColumnField';
import WorkflowStatusColumnField from './WorkflowStatusColumnField';
import AssigneeColumnField from './AssigneeColumnField';
import PriorityColumnField from './PriorityColumnField';
import UnreadUpdatesIndicator from './UnreadUpdatesIndicator';
import BillTableHeader, { BillTableFilterProperties } from './BillTableHeader';
import BillPosition from '../BillPosition/BillPosition';
import {
  democratPartyIdentifier,
  partyString,
  republicanPartyIdentifier,
  splitByDate,
} from '../../../utils';
import Info from '../../../components/svg/InfoCircleIcon';
import SparkleIcon from '../../../components/svg/SparkleIcon';
import { faBookmark, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { DecodedValueMap, QueryParamConfigMap, SetQuery } from 'use-query-params';
import { useOrganizationCustomFields } from '../../../api';
import BillCustomFieldValue from '../BillCustomField/BillCustomFieldValue';
import { serializeToReactElement } from '@enview/interface/utils/RichTextAnnotation';
import {
  DefaultBillTableColumns,
  PUBLIC_VIEW_EXCLUDED_COLUMNS,
} from '@enview/interface/types/Bill';
import { State } from '../../../dux/@types';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  ColumnDef,
  getCoreRowModel,
  getPaginationRowModel,
  PaginationState,
  SortingState,
  Updater,
  useReactTable,
} from '@tanstack/react-table';
import { isProd, SORT_BY_FOR_COLUMN } from '../../../utils/constants';
import NoBillResults from '../../LegislativeTracking/NoBillResults';
import './Tables.scss';
import PaginationBar from './PaginationBar';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import DragAlongCell from './DragAlongCell';
import DraggableTableHeader from './DraggableTableHeader';
import * as AccountAnalytics from '../../../analytics/AccountAnalytics';
// TEMPORARY: for a demo only. Remove along with the rest of the hardcoded
// regulation proof-of-concept once the demo is over.
import {
  DemoAssigneeField,
  DemoPriorityField,
  DemoTrackingField,
  DemoWorkflowStatusField,
  DemoWorkspacesField,
} from './DemoRegulationColumnFields';

const DEFAULT_PAGE_SIZE = 20;

TopBarProgress.config({
  barThickness: 4,
  barColors: {
    0: '#f0f3f8',
    0.65: '#f68c24',
    '1.0': '#f26522',
  },
  shadowColor: '#4a4a4a',
  shadowBlur: 1,
});

export type BillViewConfig = {
  readOnly?: boolean;
  columns?: string[];
  onColumnsChange?: (columns: string[]) => void;
  currentView?: BillSearchView;
  onViewChange?: (view: BillSearchView) => void;
  editingPublicConfig?: boolean;
  publicCustomFieldDefinitions?: Array<{ id: number; title: string; type: string }>;
};

// TEMPORARY: for a demo only. Remove isDemoRegulationRow and every branch
// that calls it, along with the rest of the hardcoded regulation
// proof-of-concept, once the demo is over. Lets a handful of hardcoded,
// view-only regulation rows be slapped onto real bill search results and
// rendered by this same table — ordinary bill rows never carry this flag,
// so these branches are never taken for them and the table is unchanged.
const isDemoRegulationRow = (row: BillSummary): boolean =>
  Boolean((row as unknown as { isDemoRegulation?: boolean }).isDemoRegulation);

type NewBillSource = { savedSearchId: string; savedSearchName: string };

// AI tagging records its New Bill attribution in the saved-search shape, but there is no
// saved search behind it to link to.
const isAiTagsSource = (source: NewBillSource): boolean =>
  source.savedSearchId === AI_TAGS_ATTRIBUTION_ID;

const describeSourceCount = (sources: NewBillSource[]): string => {
  const { length } = sources;
  if (sources.some(isAiTagsSource)) return `${length} source${length === 1 ? '' : 's'}`;
  return `${length} saved search${length === 1 ? '' : 'es'}`;
};

type BillTableProps = {
  results: { data: any; count: number };
  searching: boolean;
  useServerSidePagination?: boolean;
  query: DecodedValueMap<QueryParamConfigMap>;
  setQuery: SetQuery<QueryParamConfigMap>;
  showFilterProperties: boolean;
  filterProperties?: BillTableFilterProperties;
  viewConfigKey?: string;
  billViewConfig?: BillViewConfig;
  extraColumnHeaders?: Partial<Record<DefaultBillTableColumns, string>>;
  headerFilterPill?: ReactElement | null;
  // Restricts the "Source" (saved search or AI tags attribution) column to the New Bills
  // table on the What's New dashboard — see DefaultBillTableColumns.NEW_BILL_SOURCE.
  showNewBillSourceColumn?: boolean;
  // Restricts the unread-updates dot/popover on the Bill Title cell to the Updated
  // Bills / Assigned to Me sections on the What's New dashboard.
  showUnreadIndicator?: boolean;
  // Adds "Mark as Read" to Bulk Actions, for the What's New Updated Bills section.
  showMarkAsReadBulkAction?: boolean;
};

//#region BillTable
export const BillTable = (props: BillTableProps): ReactElement => {
  const {
    results,
    searching,
    useServerSidePagination,
    showFilterProperties,
    filterProperties,
    query,
    setQuery,
    viewConfigKey = 'billSearchView',
    billViewConfig,
    extraColumnHeaders,
    headerFilterPill,
    showNewBillSourceColumn = false,
    showUnreadIndicator = false,
    showMarkAsReadBulkAction = false,
  } = props;

  const {
    readOnly = false,
    columns: externalColumns,
    onColumnsChange,
    editingPublicConfig = false,
    publicCustomFieldDefinitions,
  } = billViewConfig ?? {};

  const dispatch = useDispatch();
  const teamModeId = useSelector(getTeamMode);
  const orgUser = useSelector((state: State) => state.account.organizationUser);
  const organization = useSelector(getOrgForTeamMode);
  const orgId = organization?.id;
  const canViewWorkspaceColumns = featureFlags.isFeatureEnabledForUser(
    FrontendFeature.BillTriageWorkspaceColumns,
    orgUser,
  );

  // Keep showing the last successfully loaded results while a new fetch is in
  // flight (page/page-size/filter change, or a background refetch), instead of
  // blanking the table out.
  const [displayResults, setDisplayResults] = useState(results);
  useEffect(() => {
    if (!searching) setDisplayResults(results);
  }, [results, searching]);

  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [tooltipAnchor, setTooltipAnchor] = useState('');
  const [tipContent, setTipContent] = useState<ReactElement<any>>(<></>);
  const [showTooltip, setShowTooltip] = useState(false);
  const activeAnchorRef = useRef('');

  const handleSetIsOpen = useCallback((isOpen: boolean) => {
    if (!isOpen && activeAnchorRef.current) {
      const anchor = document.getElementById(activeAnchorRef.current);
      if (anchor?.matches(':hover')) return;
    }
    if (!isOpen) activeAnchorRef.current = '';
    setShowTooltip(isOpen);
  }, []);

  const showTooltipFor = useCallback((anchorId: string, content: ReactElement) => {
    const isAnchorSwitch =
      activeAnchorRef.current !== '' && activeAnchorRef.current !== anchorId;
    activeAnchorRef.current = anchorId;
    setTipContent(content);
    setTooltipAnchor(anchorId);
    if (isAnchorSwitch) {
      setShowTooltip(false);
      requestAnimationFrame(() => setShowTooltip(true));
    } else {
      setShowTooltip(true);
    }
  }, []);

  //#region Column visibility + order
  // Use externalColumns if provided, otherwise fall back to user settings
  const userSettingsColumns =
    orgUser?.settings.billViewPreference?.views[viewConfigKey]?.columns;
  const initialColumns = externalColumns ?? userSettingsColumns;
  const defaultColumns = Object.values(DefaultBillTableColumns);

  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    initialColumns ?? defaultColumns,
  );

  const [columnOrder, setColumnOrder] = useState<string[]>(() => [
    'select-col',
    ...(initialColumns ?? defaultColumns),
  ]);

  const columnOrderMounted = useRef(false);

  useEffect(() => {
    setColumnOrder((prev) => {
      const prevWithoutSelect = prev.filter((id) => id !== 'select-col');

      const existing = prevWithoutSelect.filter((id) => selectedColumns.includes(id));
      const added = selectedColumns.filter((id) => !existing.includes(id));

      const next = ['select-col', ...existing, ...added];
      if (next.length === prev.length && next.every((id, i) => id === prev[i]))
        return prev;
      return next;
    });
  }, [selectedColumns]);

  useEffect(() => {
    if (!columnOrderMounted.current) {
      columnOrderMounted.current = true;
      return;
    }

    if (readOnly) return;

    const orderedColumns = columnOrder.filter((id) => id !== 'select-col');

    // If onColumnsChange callback is provided, use it instead of saving to user settings
    if (onColumnsChange) {
      onColumnsChange(orderedColumns);
      return;
    }

    // Otherwise save to user settings
    if (!orgUser) return;

    dispatch(
      updateOrganizationUser(
        {
          ...orgUser,
          firstName: orgUser.firstName,
          lastName: orgUser.lastName,
          email: orgUser.email,
          settings: {
            ...orgUser.settings,
            billViewPreference: {
              views: {
                ...orgUser.settings.billViewPreference?.views,
                [viewConfigKey]: {
                  columns: orderedColumns,
                  updatedAt: Date.now().toLocaleString(),
                },
              },
            },
          },
        },
        orgUser,
      ),
    );
    AccountAnalytics.trackBillViewColumnConfigChanged(orgUser.id, orderedColumns);
    dispatch(getOrganizationUser());
  }, [columnOrder, viewConfigKey, readOnly, onColumnsChange]);

  const { data: customFields = [] } = useOrganizationCustomFields(
    orgId ? orgId : skipToken,
  );

  //#region Pagination
  const queryPage = Number(query.page ?? 1);
  const queryPageSize = Number(query.pageSize ?? DEFAULT_PAGE_SIZE);

  const pagination: PaginationState = useMemo(
    () => ({
      pageIndex: queryPage - 1,
      pageSize: queryPageSize,
    }),
    [query.page, query.pageSize],
  );

  const onPaginationChange = useCallback(
    (updater) => {
      if (!useServerSidePagination) return;

      // onPaginationChange expects updater function or value, same as setState
      const next = typeof updater === 'function' ? updater(pagination) : updater;

      const page = next.pageIndex + 1;
      const pageSize = next.pageSize;

      if (page === queryPage && pageSize === queryPageSize) return;

      setQuery({ page, pageSize }, 'replaceIn');
    },
    [queryPage, queryPageSize, setQuery],
  );

  //#region Sorting
  const [sorting, setSorting] = useState<SortingState>(() =>
    SortOrder.getSortingFromOrderParam(query.order),
  );

  useEffect(() => {
    setSorting(SortOrder.getSortingFromOrderParam(query.order));
  }, [query.order]);

  const customFieldTitles = useMemo(
    () => new Set(customFields.map((f) => f.title)),
    [customFields],
  );

  const handleSortChange = (updater: Updater<SortingState>) => {
    setSorting((prev) => {
      // Updater is either ColumnSort[] or a function: (prev: ColumnSort[]) => ColumnSort[]
      const next = typeof updater === 'function' ? updater(prev) : updater;

      const active = next[0];

      if (active) {
        // Check if sorting by a custom field column
        if (customFieldTitles.has(active.id)) {
          const orderParam = SortOrder.getCustomFieldSortParam(active.id, active.desc);
          setQuery({ order: orderParam, page: 1 }, 'replaceIn');
          return next;
        }

        // maps column id to sort key
        const sortBy = SORT_BY_FOR_COLUMN[active.id];
        if (sortBy) {
          const orderParam = new SortOrder(sortBy, active.desc).getQueryParam();
          if (orderParam) {
            setQuery({ order: orderParam, page: 1 }, 'replaceIn');
          }
        }
      } else if (query.order !== SortByParam.LATEST) {
        setQuery({ order: SortByParam.LATEST, page: 1 }, 'replaceIn');
      }

      return next;
    });
  };

  //#region Bill table helpers
  const showTableResults = displayResults && displayResults.count > 0;

  const handleTooltipContentClick: MouseEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const tooltipContent = (
    primarySponsors: BillSponsor[],
    coSponsors: BillSponsor[],
  ) => {
    return (
      <div className="sponsors-tooltip" onClick={handleTooltipContentClick}>
        {!!primarySponsors.length && (
          <>
            <span className="tooltip-header">Primary sponsors:</span>
            <ul>
              {primarySponsors.map((sponsor, index) => (
                <li key={`primary-${index}`}>
                  {sponsor.person?.id ? (
                    <Link to={`/person/${sponsor.person?.id}`}>
                      {`${sponsor.name}${partyString(sponsor)}`}
                    </Link>
                  ) : (
                    `${sponsor.name}${partyString(sponsor)}`
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
        {!!coSponsors.length && (
          <>
            <span className="tooltip-header">Co-sponsors:</span>
            <ul>
              {coSponsors.map((sponsor, index) => (
                <li key={`co-${index}`}>
                  {sponsor.person?.id ? (
                    <Link to={`/person/${sponsor.person?.id}`}>
                      {`${sponsor.name}${partyString(sponsor)}`}
                    </Link>
                  ) : (
                    `${sponsor.name}${partyString(sponsor)}`
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    );
  };

  const renderBillSponsors = (bill: Bill) => {
    const { sponsors } = bill;
    if (!sponsors) return null;

    const currentSponsors = sponsors.filter((s) => !s.isRemoved);
    const primarySponsors = currentSponsors.filter((s) => s.isPrimary);
    const coSponsors = currentSponsors.filter((s) => !s.isPrimary && !s.isRemoved);

    // Some sources do not flag primary sponsors, so fall back to the first co-sponsor
    // to keep the cell consistent with the bill detail page.
    const displaySponsor = primarySponsors[0] ?? coSponsors[0];
    if (!displaySponsor) return null;

    const tooltipId = `table-tooltip-${bill.id}`;

    const handleSponsorFocus = () => {
      showTooltipFor(tooltipId, tooltipContent(primarySponsors, coSponsors));
    };

    return (
      <p
        className="bill-sponsor-table-cell"
        id={tooltipId}
        key={bill.id}
        onClick={handleSponsorFocus}
        onMouseEnter={handleSponsorFocus}
      >
        <span>{displaySponsor.name + partyString(displaySponsor)}</span>
        <Info className="ml-1" color="#4a4a4a" size={16} />
      </p>
    );
  };

  const sourceTooltipContent = (
    sources: { savedSearchId: string; savedSearchName: string }[],
  ) => {
    return (
      <div className="source-tooltip" onClick={handleTooltipContentClick}>
        <span className="tooltip-header">
          {sources.some(isAiTagsSource) ? 'Sources:' : 'Saved searches:'}
        </span>
        <ul>
          {sources.map((source) => (
            <li key={source.savedSearchId}>
              {isAiTagsSource(source) ? (
                source.savedSearchName
              ) : (
                <Link to={`/legislative-tracking/saved-search/${source.savedSearchId}`}>
                  {source.savedSearchName}
                </Link>
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderNewBillSource = (bill: BillSummary) => {
    if (isDemoRegulationRow(bill)) {
      const agency = (bill as unknown as { sourceAgency?: string }).sourceAgency;
      if (!agency) return null;
      const demoSources = [{ savedSearchId: bill.id, savedSearchName: agency }];
      const tooltipId = `new-bill-source-${bill.id}`;
      const handleSourceFocus = (): void => {
        showTooltipFor(tooltipId, sourceTooltipContent(demoSources));
      };
      return (
        <div className="new-bill-source-cell">
          <span className="new-bill-source-name" title={agency}>
            <FontAwesomeIcon icon={faBookmark} size="xs" />
            <span>{agency}</span>
          </span>
          <span
            className="new-bill-source-count"
            id={tooltipId}
            onMouseEnter={handleSourceFocus}
          >
            1 source
            <FontAwesomeIcon icon={faChevronDown} size="2xs" />
          </span>
        </div>
      );
    }

    const sources = bill.newBillSources;
    if (!sources?.length) return null;

    const [primarySource] = sources;
    const aiTagsSource = sources.find(isAiTagsSource);
    const tooltipId = `new-bill-source-${bill.id}`;

    const handleSourceFocus = (): void => {
      showTooltipFor(tooltipId, sourceTooltipContent(sources));
    };
    const sourceCount = (
      <span
        className="new-bill-source-count"
        id={tooltipId}
        onMouseEnter={handleSourceFocus}
      >
        {describeSourceCount(sources)}
        <FontAwesomeIcon icon={faChevronDown} size="2xs" />
      </span>
    );

    // AI tags lead, with the bill's tags listed under them. The source count only appears
    // when saved searches found the bill too.
    if (aiTagsSource) {
      return (
        <div className="new-bill-source-cell">
          <span className="new-bill-source-name">
            <SparkleIcon color="currentColor" size={10} />
            <span>AI tags</span>
          </span>
          <span className="new-bill-source-tags">
            {parseAiTagsSourceName(aiTagsSource.savedSearchName).map((tag) => (
              <span key={tag}>{tag}</span>
            ))}
          </span>
          {sources.length > 1 && sourceCount}
        </div>
      );
    }

    return (
      <div className="new-bill-source-cell">
        <span className="new-bill-source-name" title={primarySource.savedSearchName}>
          <FontAwesomeIcon icon={faBookmark} size="xs" />
          <span>{primarySource.savedSearchName}</span>
        </span>
        {sourceCount}
      </div>
    );
  };

  const renderLatestCommitteeHearing = (bill: BillSummary) => {
    const { hearings } = bill;
    if (!hearings || hearings.length === 0) return null;

    const { pastEvents } = splitByDate(hearings);
    if (pastEvents.length === 0) return null;

    const latestPast = pastEvents.reduce((latest, curr) =>
      new Date(curr.start).getTime() > new Date(latest.start).getTime() ? curr : latest,
    );

    const hearingDate = formatDate(latestPast.start, 'short');
    const hearingId = 'latest-committee-hearing-' + bill.sourceBillId;
    const hearingContent = `${hearingDate} - ${latestPast.eventData.name}`;

    const handleFocus = () => {
      showTooltipFor(hearingId, <p>{hearingContent}</p>);
    };

    return (
      <span id={hearingId} onClick={handleFocus} onMouseEnter={handleFocus}>
        {hearingContent}
      </span>
    );
  };

  const renderUpcomingHearings = (bill: BillSummary) => {
    const { hearings } = bill;
    if (!hearings || hearings.length === 0) return null;

    const { upcomingEvents, pastEvents } = splitByDate(hearings);
    let displayedEvent: Hearing;

    if (upcomingEvents.length > 0) {
      displayedEvent = upcomingEvents.reduce((nearest, curr) =>
        new Date(curr.start).getTime() < new Date(nearest.start).getTime()
          ? curr
          : nearest,
      );
    } else {
      displayedEvent = pastEvents.reduce((latest, curr) =>
        new Date(curr.start).getTime() > new Date(latest.start).getTime()
          ? curr
          : latest,
      );
    }

    const hearingDate = formatDate(displayedEvent.start, 'short');
    const upcomingHearingsId = 'upcoming-hearing' + bill.sourceBillId;
    const upcomingEventContent = `${hearingDate} - ${displayedEvent.eventData.name}`;

    const handleUpcomingHearingFocus = () => {
      showTooltipFor(upcomingHearingsId, <p>{upcomingEventContent}</p>);
    };

    return (
      <span
        id={upcomingHearingsId}
        onClick={handleUpcomingHearingFocus}
        onMouseEnter={handleUpcomingHearingFocus}
      >
        {upcomingEventContent}
      </span>
    );
  };

  //#region Columns
  const hasNewTrackingButton = featureFlags.isFeatureEnabledForUser(
    FrontendFeature.NewTrackingButton,
    orgUser,
  );
  const columns = useMemo<ColumnDef<BillSummary>[]>(() => {
    const selectionColumn: ColumnDef<BillSummary> = {
      id: 'select-col',
      size: 40,
      enableSorting: false,
      header: ({ table }) => {
        const isAllSelected = table.getIsAllRowsSelected();
        const isSomeSelected = table.getIsSomeRowsSelected();
        return (
          <input
            aria-label="Select all rows on this page"
            checked={isAllSelected}
            onChange={table.getToggleAllRowsSelectedHandler()}
            ref={(el) => {
              if (!el) return;
              el.indeterminate = !isAllSelected && isSomeSelected;
            }}
            type="checkbox"
          />
        );
      },
      cell: ({ row }) => (
        <input
          aria-label="Select row"
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
          type="checkbox"
        />
      ),
    };

    const baseColumns: ColumnDef<BillSummary>[] = [
      {
        header: 'Tracking',
        id: DefaultBillTableColumns.TRACKING,
        size: 150,
        enableSorting: false,
        minSize: 110,
        cell: ({ row }) => {
          if (isDemoRegulationRow(row.original)) return <DemoTrackingField />;
          return hasNewTrackingButton ? (
            <TrackColumnField bill={row.original as Bill} />
          ) : (
            <BillTrackingButton
              bill={row.original as Bill}
              hasStatusIndicator
              isCompactMode
            />
          );
        },
      },
      {
        id: DefaultBillTableColumns.TAGS,
        header: 'Tags',
        size: 50,
        accessorKey: 'tags',
        enableSorting: true,
        meta: { align: 'center' },
        cell: ({ row }) => {
          if (isDemoRegulationRow(row.original)) return null;
          return <BillTagButton bill={row.original as Bill} hasStatusIndicator />;
        },
      },
      {
        header: 'Position',
        id: DefaultBillTableColumns.POSITION,
        size: 50,
        cell: ({ row }) => {
          if (isDemoRegulationRow(row.original)) return null;
          return <BillPosition bill={row.original as Bill} />;
        },
      },
      {
        header: 'Bill ID',
        id: DefaultBillTableColumns.BILL_ID,
        accessorKey: 'id',
        enableSorting: true,
        maxSize: 200,
        cell: ({ row }) => {
          if (isDemoRegulationRow(row.original)) {
            return (
              <Link className="bill-link" to="/">
                {row.original.id}
              </Link>
            );
          }
          const bill = row.original as Bill;
          const title = convertIdToAbbreviatedTitle(bill.id);
          const billAlias = getBillAlias(bill.userAliases, teamModeId);
          return (
            <Link
              className="bill-link"
              to={`/legislative-tracking/bill/details/${bill.id}`}
            >
              {title}
              {billAlias ? `(${billAlias.alias})` : ''}
            </Link>
          );
        },
      },
      {
        header: 'Bill ID (Source)',
        id: DefaultBillTableColumns.SOURCE_LINK,
        enableSorting: false,
        maxSize: 200,
        cell: ({ row }) => {
          const bill = row.original as Bill;
          if (isDemoRegulationRow(row.original)) {
            if (!bill.sourceUrl) return null;
            return (
              <a href={bill.sourceUrl} rel="noreferrer" target="_blank">
                Source
              </a>
            );
          }
          const title = convertIdToAbbreviatedTitle(bill.id);
          if (!bill.sourceUrl) return <span>{title}</span>;
          return (
            <a href={bill.sourceUrl} rel="noreferrer" target="_blank">
              {title}
            </a>
          );
        },
      },
      {
        header: 'Bill Title',
        id: DefaultBillTableColumns.BILL_TITLE,
        accessorKey: 'name',
        enableSorting: true,
        minSize: 200,
        cell: ({ row }) =>
          showUnreadIndicator ? (
            <UnreadUpdatesIndicator bill={row.original} />
          ) : (
            <span className="bill-text-truncated">{row.original.name}</span>
          ),
      },
      {
        header: 'Bill Sponsors',
        id: DefaultBillTableColumns.BILL_SPONSORS,
        enableSorting: false,
        minSize: 150,
        maxSize: 300,
        cell: ({ row }) => renderBillSponsors(row.original as Bill),
      },
      {
        header: '(D)',
        id: DefaultBillTableColumns.DEMOCRATS,
        accessorKey: 'democrats',
        enableSorting: true,
        minSize: 50,
        maxSize: 75,
        cell: ({ row }) => {
          const bill = row.original as Bill;
          const { sponsors } = bill;
          if (!sponsors) return null;
          const count = sponsors.filter(
            (sponsor) => partyString(sponsor) === democratPartyIdentifier,
          ).length;
          return <p className="text-center">{count}</p>;
        },
      },
      {
        header: '(R)',
        id: DefaultBillTableColumns.REPUBLICANS,
        accessorKey: 'republicans',
        enableSorting: true,
        minSize: 50,
        maxSize: 75,
        cell: ({ row }) => {
          const bill = row.original as Bill;
          const { sponsors } = bill;
          if (!sponsors) return null;
          const count = sponsors.filter(
            (sponsor) => partyString(sponsor) === republicanPartyIdentifier,
          ).length;
          return <p className="text-center">{count}</p>;
        },
      },
      {
        header: 'Bill Status',
        id: DefaultBillTableColumns.BILL_STATUS,
        accessorKey: 'status',
        enableSorting: true,
        minSize: 130,
        maxSize: 130,
        cell: ({ row }) => {
          if (isDemoRegulationRow(row.original)) {
            const label = (
              row.original as unknown as { demoRegulationStatusLabel?: string }
            ).demoRegulationStatusLabel;
            return <span className="demo-regulation-badge">{label}</span>;
          }
          const bill = row.original as Bill;
          return (
            <div>
              <BillStatusBadge bill={bill} isCompactMode />
            </div>
          );
        },
      },
      {
        header: 'Jurisdiction',
        id: DefaultBillTableColumns.JURISDICTION,
        enableSorting: false,
        maxSize: 150,
        cell: ({ row }) => row.original.session.state.name,
      },
      {
        header: 'Session',
        id: DefaultBillTableColumns.SESSION,
        enableSorting: true,
        maxSize: 150,
        accessorKey: 'session.displayName',
        cell: ({ row }) => row.original.session.displayName,
      },
      {
        header: 'Latest Action',
        id: DefaultBillTableColumns.LATEST_ACTION,
        enableSorting: false,
        maxSize: 150,
        cell: ({ row }) => (
          <span className="bill-text-truncated">
            {getMostRecentAction(row.original)?.description}
          </span>
        ),
      },
      {
        header: 'Action Date',
        id: DefaultBillTableColumns.ACTION_DATE,
        enableSorting: true,
        accessorKey: 'action-date',
        minSize: 115,
        maxSize: 150,
        cell: ({ row }) => {
          const recentAction = getMostRecentAction(row.original);
          return recentAction ? formatDate(recentAction.actionDate, 'short') : '';
        },
      },
      {
        header: 'Upcoming Hearing',
        id: DefaultBillTableColumns.UPCOMING_HEARING,
        maxSize: 300,
        accessorKey: 'hearings',
        enableSorting: true,
        cell: ({ row }) => (
          <span className="bill-text-truncated">
            {renderUpcomingHearings(row.original)}
          </span>
        ),
      },
      {
        header: 'Latest Committee Hearing',
        id: DefaultBillTableColumns.LATEST_COMMITTEE_HEARING,
        maxSize: 300,
        enableSorting: false,
        cell: ({ row }) => (
          <span className="bill-text-truncated">
            {renderLatestCommitteeHearing(row.original)}
          </span>
        ),
      },
      {
        header: 'Workspaces',
        id: DefaultBillTableColumns.WORKSPACES,
        enableSorting: false,
        minSize: 150,
        maxSize: 300,
        cell: ({ row }) => {
          if (isDemoRegulationRow(row.original)) return <DemoWorkspacesField />;
          return (
            <WorkspacesColumnField
              billId={row.original.id}
              billWorkspaces={row.original.workspaces}
            />
          );
        },
      },
      {
        header: 'Workflow Status',
        id: DefaultBillTableColumns.WORKFLOW_STATUS,
        enableSorting: false,
        minSize: 150,
        maxSize: 300,
        cell: ({ row }) => {
          if (isDemoRegulationRow(row.original)) return <DemoWorkflowStatusField />;
          return (
            <WorkflowStatusColumnField
              billId={row.original.id}
              billWorkspaces={row.original.workspaces}
              customFields={row.original.customFields}
            />
          );
        },
      },
      {
        header: 'Assignee',
        id: DefaultBillTableColumns.ASSIGNEE,
        enableSorting: false,
        minSize: 150,
        maxSize: 300,
        cell: ({ row }) => {
          if (isDemoRegulationRow(row.original)) return <DemoAssigneeField />;
          return (
            <AssigneeColumnField
              billId={row.original.id}
              billWorkspaces={row.original.workspaces}
              customFields={row.original.customFields}
            />
          );
        },
      },
      {
        header: 'Priority',
        id: DefaultBillTableColumns.PRIORITY,
        enableSorting: false,
        minSize: 150,
        maxSize: 300,
        cell: ({ row }) => {
          if (isDemoRegulationRow(row.original)) return <DemoPriorityField />;
          return (
            <PriorityColumnField
              billId={row.original.id}
              billWorkspaces={row.original.workspaces}
              customFields={row.original.customFields}
            />
          );
        },
      },
      {
        header: 'Source',
        id: DefaultBillTableColumns.NEW_BILL_SOURCE,
        enableSorting: false,
        maxSize: 300,
        cell: ({ row }) => renderNewBillSource(row.original),
      },
    ];

    // Custom field columns: use publicCustomFieldDefinitions for public views,
    // or filter to only public fields when editing public config
    const customColumns: ColumnDef<BillSummary>[] =
      readOnly && publicCustomFieldDefinitions
        ? publicCustomFieldDefinitions.map((fieldDef) => ({
            header: fieldDef.title,
            id: fieldDef.title,
            minSize: 230,
            enableSorting: false,
            accessorKey: fieldDef.title,
            cell: ({ row }) => {
              const bill = row.original;
              const fieldValue = bill.publicCustomFields?.[fieldDef.title];
              const richText = fieldValue?.fieldValue?.richText;
              if (!richText) return null;
              const content = serializeToReactElement(richText);
              const cfTooltipId = `cf-public-${fieldDef.id}-${bill.sourceBillId}`;
              const handleHover = () => {
                showTooltipFor(cfTooltipId, content);
              };
              return (
                <span
                  className="bill-text-truncated"
                  id={cfTooltipId}
                  onClick={handleHover}
                  onMouseEnter={handleHover}
                >
                  {content}
                </span>
              );
            },
          }))
        : orgId
        ? customFields
            // System fields (Workflow Status, Priority, Assignee) have their own
            // dedicated columns above; excluded here to avoid a duplicate generic
            // rich-text column when SystemCustomFields is enabled for the viewer.
            .filter(
              (field) => !field.isSystem && (!editingPublicConfig || field.isPublic),
            )
            .map((field) => ({
              header: field.title,
              id: field.title,
              minSize: 230,
              enableSorting: true,
              accessorKey: field.title,
              cell: ({ row }) => {
                const bill = row.original;
                const cfTooltipId = `cf-${field.id}-${bill.sourceBillId}`;
                return (
                  <BillCustomFieldValue
                    bill={bill}
                    field={field}
                    onHover={(content, anchorId) => {
                      showTooltipFor(anchorId, content);
                    }}
                    tooltipId={cfTooltipId}
                  />
                );
              },
            }))
        : [];

    // Workspaces/Workflow Status/Assignee/Priority are still bill-triage-epic-only,
    // civic-eagle-staff-only features — Tracking is excluded from this gate since it's
    // an existing, already-GA'd control the epic only added a dropdown caret to, not
    // new functionality.
    const visibleBaseColumns = baseColumns.filter((col) => {
      if (
        !canViewWorkspaceColumns &&
        (col.id === DefaultBillTableColumns.WORKSPACES ||
          col.id === DefaultBillTableColumns.WORKFLOW_STATUS ||
          col.id === DefaultBillTableColumns.ASSIGNEE ||
          col.id === DefaultBillTableColumns.PRIORITY)
      ) {
        return false;
      }
      if (
        col.id === DefaultBillTableColumns.NEW_BILL_SOURCE &&
        !showNewBillSourceColumn
      ) {
        return false;
      }
      return true;
    });

    const columnMap = [...visibleBaseColumns, ...customColumns].reduce<
      Record<string, ColumnDef<BillSummary>>
    >((acc, col) => {
      if (col.id) acc[col.id] = col;
      return acc;
    }, {});

    const excludedColumns = PUBLIC_VIEW_EXCLUDED_COLUMNS as readonly string[];
    const isPublicView = readOnly || editingPublicConfig;
    const visibleDataColumns = selectedColumns
      .filter((id) => !isPublicView || !excludedColumns.includes(id))
      .map((id) => columnMap[id])
      .filter((col): col is ColumnDef<BillSummary> => Boolean(col));

    // Hide selection column in read-only or public-view mode
    if (isPublicView) {
      return visibleDataColumns;
    }

    return [selectionColumn, ...visibleDataColumns];
  }, [
    customFields,
    selectedColumns,
    teamModeId,
    orgId,
    readOnly,
    publicCustomFieldDefinitions,
    editingPublicConfig,
    canViewWorkspaceColumns,
    hasNewTrackingButton,
    showNewBillSourceColumn,
    showUnreadIndicator,
  ]);

  //#region TanStack table
  const table = useReactTable({
    data: displayResults?.data ?? [],
    columns,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: { rowSelection, pagination, sorting, columnOrder },
    // row selection
    onRowSelectionChange: setRowSelection,
    enableRowSelection: (row) => !isDemoRegulationRow(row.original),
    enableMultiRowSelection: true,
    // pagination
    manualPagination: !!useServerSidePagination,
    onPaginationChange: onPaginationChange,
    pageCount: useServerSidePagination
      ? Math.ceil((displayResults?.count ?? 0) / (pagination.pageSize || 1))
      : undefined,
    // sorting
    manualSorting: true,
    onSortingChange: handleSortChange,
    enableSorting: true,
    enableMultiSort: false,
    // DnD
    onColumnOrderChange: setColumnOrder,
    debugTable: !isProd,
    debugHeaders: !isProd,
    debugColumns: !isProd,
  });

  const selectedBills = table
    .getSelectedRowModel()
    .flatRows.map((r) => r.original as Bill);

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setColumnOrder((colOrder) => {
        const oldIndex = colOrder.indexOf(active.id as string);
        const newIndex = colOrder.indexOf(over.id as string);
        return arrayMove(colOrder, oldIndex, newIndex);
      });
    }
  }

  //#region UI
  return (
    <div className="list results-pane">
      <BillTableHeader
        billViewConfig={billViewConfig}
        extraColumnHeaders={extraColumnHeaders}
        filterProperties={filterProperties}
        headerFilterPill={headerFilterPill}
        query={query}
        results={displayResults}
        selectedColumns={selectedColumns}
        selectedRows={selectedBills}
        setQuery={setQuery}
        setSelectedColumns={setSelectedColumns}
        showFilterProperties={showFilterProperties}
        showMarkAsReadBulkAction={showMarkAsReadBulkAction}
        clearSelectedRows={() => setRowSelection({})}
      />

      {searching && <TopBarProgress />}

      {searching && !showTableResults && (
        <div className="table-spinner-overlay">
          <Spinner animation="border" role="status"></Spinner>
        </div>
      )}

      {showTableResults && (
        <div className="tanstack-table bill-table filtered-table">
          <ReactTooltip
            anchorId={tooltipAnchor}
            className="tooltip"
            clickable
            delayHide={1000}
            isOpen={showTooltip}
            place="bottom"
            setIsOpen={handleSetIsOpen}
          >
            {tipContent}
          </ReactTooltip>

          <PaginationBar
            pagination={pagination}
            results={displayResults}
            table={table}
          />

          <div className="table-wrapper">
            <DndContext
              collisionDetection={closestCenter}
              modifiers={[restrictToHorizontalAxis]}
              onDragEnd={handleDragEnd}
              sensors={sensors}
            >
              <table>
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      <SortableContext
                        items={columnOrder}
                        strategy={horizontalListSortingStrategy}
                      >
                        {headerGroup.headers.map((header) => (
                          <DraggableTableHeader
                            header={header}
                            key={header.id}
                            readOnly={readOnly}
                          />
                        ))}
                      </SortableContext>
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <SortableContext
                          items={columnOrder}
                          key={cell.id}
                          strategy={horizontalListSortingStrategy}
                        >
                          <DragAlongCell cell={cell} key={cell.id} />
                        </SortableContext>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </DndContext>
          </div>
        </div>
      )}

      {(!displayResults || displayResults?.count === 0) && <NoBillResults />}
    </div>
  );
};

export default BillTable;
